import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BasicDoctor, FetchInstitutionByIDResponse, FetchInstitutionsQueryParam, Institution, InstitutionList, InstitutionListItem, InstitutionListResponse, InstitutionPayload, InstitutionSelectorRequest, InstitutionSelectorResponse, InstitutionSimpleListResponse, InstitutionStudy } from '@shared/models';
import { BehaviorSubject, Observable, map, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class InstitutionService {
  _apiUrl = environment.apiUrl;
  private _http = inject(HttpClient);
  private jsonUrl = 'assets/mock-data/institutions.json';
  private _authHeaders = new HttpHeaders({
    Authorization: '',
  });

  #institutionsList = new BehaviorSubject<Institution[]>([]);

  get institutionsList(): Observable<Institution[]> {
    return this.#institutionsList.asObservable();
  }

  public fetchInstitutions(
    queryParams?: Partial<FetchInstitutionsQueryParam>
  ): Observable<InstitutionList> {
    let params = new HttpParams();

    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value) {
          params = params.set(key, value);
        }
      });
    }

    return this._http
      .get<InstitutionListResponse>(`${this._apiUrl}/api/institution/list`, {
        headers: this._authHeaders,
        params,
      })
      .pipe(
        map((resp) => {
          const institutionList: InstitutionList = {
            pagination: resp.pagination,
            institution_list: resp.data.map((institutionResponse) => {
              const institution: InstitutionListItem = {
                id: institutionResponse?.id,
                name: institutionResponse?.name,
                phone_number: institutionResponse?.phone_number ? String(institutionResponse.phone_number) : null,
                address: institutionResponse?.address ?? null,
              };
              return institution;
            }),
          };
          return institutionList;
        })
      );
  }

  searchInstitution(search: string): void {
    this._http
      .get(`${this._apiUrl}/api/institution/list`, {
        headers: this._authHeaders,
        params: new HttpParams().set('search', search),
      })
      .pipe(
        tap((resp: any) => {
          this.#institutionsList.next(resp.data);
        })
      )
      .subscribe();
  }

  public createInstitution(
    institutionForm: InstitutionPayload
  ): Observable<any> {

    const payload: InstitutionPayload = {
      name: institutionForm.name,
      phone_number: institutionForm.phone_number ?? null,
      address: institutionForm.address ?? null,
    };

    return this._http.post<any>(
      `${this._apiUrl}/api/institution/register`,
      payload,
      { headers: this._authHeaders }
    );
  }

  public updateInstitution(
    id: number,
    institutionForm: InstitutionPayload
  ): Observable<any> {
    const payload: InstitutionPayload = {
      name: institutionForm.name,
      phone_number: institutionForm.phone_number ?? null,
      address: institutionForm.address ?? null,
    };

    return this._http.patch<any>(
      `${this._apiUrl}/api/institution/${id}`,
      payload,
      { headers: this._authHeaders }
    );
  }

  public fetchInstitutionByID(
    id: number
  ): Observable<FetchInstitutionByIDResponse> {
    return this._http.get<FetchInstitutionByIDResponse>(
      `${this._apiUrl}/api/institution/${id}`,
      { headers: this._authHeaders }
    );
  }

  getTotalMedics(): Observable<number> {
    return this._http
      .get<any>(
        `${this._apiUrl}/api/medic/simple-list?current_page=1&per_page=1`,
        {
          headers: this._authHeaders,
        }
      )
      .pipe( 
        map((resp) => resp.pagination.total || 0) 
      );
  }

  getAssociatedMedicsList(): Observable<BasicDoctor[]> {
    return this.getTotalMedics().pipe(
      switchMap((totalMedics) => {
        return this._http.get<any>(
          `${this._apiUrl}/api/medic/simple-list?current_page=1&per_page=${totalMedics}`,
          { headers: this._authHeaders }
        );
      }),
      map((resp: any) => resp.data)
    );
  }
  
  public fetchInstitutionsByMedic(
    request: InstitutionSelectorRequest
  ): Observable<InstitutionSelectorResponse> {
    return this._http.get<InstitutionStudy[]>(this.jsonUrl).pipe(
      map((institutions) => {
        const filteredInstitutions = institutions.filter((inst) =>
          inst.medic_ids.some((medic) => medic.medic_id === request.medic_id)
        );
        return { institutions: filteredInstitutions };
      })
    );
  }

  fetchInstitutionsByDoctorId(
    doctorId: number
  ): Observable<InstitutionStudy[]> {
   
    return this._http
      .get<InstitutionSelectorResponse>(
        `${this._apiUrl}/api/institutions/${doctorId}`
      )
      .pipe(map((response) => response.institutions));
  }

  fetchInstitutionsSimpleList(name?: string): Observable<InstitutionSimpleListResponse> {
    let params = new HttpParams().set('list_format', 'input');
    if (name && name.trim().length > 0) {
      params = params.set('name', name.trim());
    }

    return this._http.get<InstitutionSimpleListResponse>(
      `${this._apiUrl}/api/institution/simple-list`,
      {
        headers: this._authHeaders,
        params,
      }
    );
  }

  public deleteInstitution(id: number): Observable<any> {
    return this._http.delete<any>(
      `${this._apiUrl}/api/institution/${id}`,
      { headers: this._authHeaders }
    );
  }
}
